<script lang="ts">
	import { devices } from 'src/stores/lists/devices';
	import { edit } from 'src/stores/ui/edit_lists';
	import {
		getCameraDeviceFromCard,
		isCameraSlotUsingLiveFeed,
		normalizeCameraConfig,
		resolveCameraLayout,
		type DashCameraWidgetCardRef,
	} from 'src/components/Dashboards/dash-camera-helpers';
	import { mapDashWidgetToEditItem } from 'src/components/Dashboards/dash-helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import type {
		DashCameraWidgetConfig,
		DashWidgetListItem,
	} from 'src/components/Dashboards/dash-types';
	import DashWidgetActionModal from 'src/components/AddActions/Wizards/DashWidgetActionModal.svelte';
	import DashCameraWidgetCell from './DashCameraWidgetCell.svelte';
	import DashCameraMissingSlot from './DashCameraMissingSlot.svelte';

	export let cards: DashCameraWidgetCardRef[] = [];
	export let cameraConfig: DashCameraWidgetConfig = {};
	export let widgetItem: DashWidgetListItem | undefined;

	let focusedCameraId = '';
	let showEditWidgetModal = false;
	$: normalizedConfig = normalizeCameraConfig(cameraConfig);
	$: sortedCards = (cards || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
	$: layout = resolveCameraLayout(normalizedConfig, sortedCards.length);
	$: primaryCard = sortedCards[0];
	$: currentFocusedId =
		focusedCameraId || normalizedConfig.focusedCameraId || primaryCard?.id?.toString() || '';
	$: focusCard = sortedCards.find((card) => card.id?.toString() === currentFocusedId) || primaryCard;
	$: previewCards = sortedCards.filter((card) => card.id?.toString() !== focusCard?.id?.toString());
	$: isEditing = $edit;
	$: isTripleFocusLayout = layout === 'triple-focus';
	$: cameraCount = sortedCards.length;
	$: primaryDevice = getCameraDeviceFromCard(primaryCard, $devices);
	$: focusDevice = getCameraDeviceFromCard(focusCard, $devices);
	$: showMissingSlotCta = Boolean(widgetItem);
	$: gridFillsSingleColumn = sortedCards.length === 1;

	$: focusUsesLive = focusCard
		? isCameraSlotUsingLiveFeed(focusCard, 'main', normalizedConfig, $devices, cameraCount)
		: false;

	$: dashWidgetItemEdit = widgetItem ? mapDashWidgetToEditItem(widgetItem) : undefined;

	function openCamera(card?: DashCameraWidgetCardRef, event?: Event) {
		if (isEditing || !card?.id) return;
		event?.stopPropagation();
		setSidePanelState({ type: 'device', id: card.id.toString(), open: true });
	}

	function focusCamera(card?: DashCameraWidgetCardRef, event?: Event) {
		if (isEditing || !card?.id) return;
		event?.stopPropagation();
		const nextFocusId = card.id.toString();
		if (nextFocusId === currentFocusedId) return;
		focusedCameraId = nextFocusId;
	}

	function handleCardAreaClick(
		card?: DashCameraWidgetCardRef,
		role: 'main' | 'side' = 'main',
		event?: Event,
	) {
		if (isTripleFocusLayout && role === 'side') {
			focusCamera(card, event);
			return;
		}
		openCamera(card, event);
	}

	function slotLive(card: DashCameraWidgetCardRef | undefined, role: 'main' | 'side') {
		return (
			!isEditing && isCameraSlotUsingLiveFeed(card, role, normalizedConfig, $devices, cameraCount)
		);
	}

	function openWidgetSelectItems() {
		if (!widgetItem) return;
		showEditWidgetModal = true;
	}
</script>

{#if layout === 'single' && primaryCard && primaryDevice}
	<div class="camera-single-cell">
		<DashCameraWidgetCell
			device={primaryDevice}
			liveFeed={slotLive(primaryCard, 'main')}
			allowFullscreen={!isEditing}
			on:openDevice={() => openCamera(primaryCard)}
		/>
	</div>
{:else if layout === 'single' && primaryCard}
	<div class="camera-single-cell">
		<DashCameraMissingSlot showEditCta={showMissingSlotCta} on:edit={openWidgetSelectItems} />
	</div>
{:else if layout === 'pair' || layout === 'quad'}
	<div class="camera-grid-layout" class:single-col={gridFillsSingleColumn}>
		{#each sortedCards as card (`grid-${card.id}`)}
			{@const device = getCameraDeviceFromCard(card, $devices)}
			{#if device}
				<div
					class="camera-layout-cell"
					class:edit-static={isEditing}
					role="button"
					tabindex={isEditing ? -1 : 0}
					aria-disabled={isEditing}
					on:click={(e) => handleCardAreaClick(card, 'main', e)}
					on:keydown={(e) => e.key === 'Enter' && handleCardAreaClick(card, 'main', e)}
				>
					<DashCameraWidgetCell
						{device}
						liveFeed={slotLive(card, 'main')}
						allowFullscreen={!isEditing}
						denseOverlay
						on:openDevice={() => openCamera(card)}
					/>
				</div>
			{:else}
				<DashCameraMissingSlot
					showEditCta={showMissingSlotCta}
					on:edit={openWidgetSelectItems}
				/>
			{/if}
		{/each}
	</div>
{:else if isTripleFocusLayout}
	<div class="camera-trio-widget triple-focus">
		{#if focusCard && focusDevice}
			<div
				class="triple-main"
				class:edit-static={isEditing}
				role="button"
				tabindex={isEditing ? -1 : 0}
				aria-disabled={isEditing}
				on:click={(e) => handleCardAreaClick(focusCard, 'main', e)}
				on:keydown={(e) => e.key === 'Enter' && handleCardAreaClick(focusCard, 'main', e)}
			>
				<DashCameraWidgetCell
					device={focusDevice}
					liveFeed={focusUsesLive}
					allowFullscreen={!isEditing}
					on:openDevice={() => openCamera(focusCard)}
				/>
			</div>
		{:else if focusCard}
			<DashCameraMissingSlot showEditCta={showMissingSlotCta} on:edit={openWidgetSelectItems} />
		{/if}

		<div class="triple-side">
			{#each previewCards as card (`slot-${card.id}`)}
				{@const device = getCameraDeviceFromCard(card, $devices)}
				{#if device}
					<div
						class="triple-preview"
						class:edit-static={isEditing}
						role="button"
						tabindex={isEditing ? -1 : 0}
						aria-disabled={isEditing}
						on:click={(e) => handleCardAreaClick(card, 'side', e)}
						on:keydown={(e) => e.key === 'Enter' && handleCardAreaClick(card, 'side', e)}
					>
						<DashCameraWidgetCell
							{device}
							liveFeed={slotLive(card, 'side')}
							allowFullscreen={false}
							denseOverlay
							on:openDevice={() => openCamera(card)}
						/>
					</div>
				{:else}
					<DashCameraMissingSlot
						showEditCta={showMissingSlotCta}
						on:edit={openWidgetSelectItems}
					/>
				{/if}
			{/each}
		</div>
	</div>
{/if}

{#if showEditWidgetModal && dashWidgetItemEdit}
	<DashWidgetActionModal
		bind:showModal={showEditWidgetModal}
		dashEditItem={dashWidgetItemEdit}
		initialStep="select-items"
	/>
{/if}

<style>
	.camera-single-cell {
		width: 100%;
		min-width: 0;
		aspect-ratio: 16 / 9;
	}

	.camera-grid-layout {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--spacing-s);
		width: 100%;
	}

	.camera-grid-layout.single-col {
		grid-template-columns: 1fr;
	}

	.camera-layout-cell {
		width: 100%;
		min-width: 0;
		aspect-ratio: 16 / 9;
		padding: 0;
		border: 0;
		border-radius: var(--radius-big);
		background: transparent;
		overflow: hidden;
		cursor: pointer;
	}

	.camera-single-cell :global(.camera-tile-root),
	.camera-layout-cell :global(.camera-tile-root),
	.triple-main :global(.camera-tile-root),
	.triple-preview :global(.camera-tile-root) {
		width: 100%;
		height: 100%;
	}

	.camera-single-cell :global(.camera-tile-widget),
	.camera-layout-cell :global(.camera-tile-widget),
	.triple-main :global(.camera-tile-widget),
	.triple-preview :global(.camera-tile-widget) {
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
	}

	.camera-single-cell :global(.video-container-wrapper),
	.camera-single-cell :global(.video-player-area),
	.camera-single-cell :global(.zoom-container),
	.camera-layout-cell :global(.video-container-wrapper),
	.camera-layout-cell :global(.video-player-area),
	.camera-layout-cell :global(.zoom-container),
	.triple-main :global(.video-container-wrapper),
	.triple-main :global(.video-player-area),
	.triple-main :global(.zoom-container),
	.triple-preview :global(.video-container-wrapper),
	.triple-preview :global(.video-player-area),
	.triple-preview :global(.zoom-container) {
		height: 100%;
	}

	.camera-single-cell :global(.video-player-area video),
	.camera-single-cell :global(.state-placeholder),
	.camera-layout-cell :global(.video-player-area video),
	.camera-layout-cell :global(.state-placeholder),
	.triple-main :global(.video-player-area video),
	.triple-main :global(.state-placeholder),
	.triple-preview :global(.video-player-area video),
	.triple-preview :global(.state-placeholder) {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.camera-trio-widget.triple-focus {
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
		gap: var(--spacing-s);
		width: 100%;
		align-items: start;
	}

	.camera-trio-widget.triple-focus .triple-side {
		grid-template-columns: 1fr;
		grid-template-rows: auto auto;
	}

	.triple-main,
	.triple-preview {
		display: block;
		width: 100%;
		min-width: 0;
		aspect-ratio: 16 / 9;
		padding: 0;
		border: 0;
		border-radius: var(--radius-big);
		background: transparent;
		overflow: hidden;
		cursor: pointer;
		text-align: left;
	}

	.triple-side {
		display: grid;
		grid-template-rows: auto auto;
		gap: var(--spacing-s);
		align-content: start;
		min-width: 0;
	}

	.triple-main.edit-static,
	.triple-preview.edit-static,
	.camera-layout-cell.edit-static {
		pointer-events: none;
		cursor: default;
	}

	@media (max-width: 1125px) {
		.camera-trio-widget.triple-focus {
			grid-template-columns: 1fr;
		}

		.camera-trio-widget.triple-focus .triple-side {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			grid-template-rows: auto;
		}
	}
</style>
